import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import styles from './base-item-card.module.css';
import { IItinerary2 } from '../../../Models/itineraries/itinerary.model';
import { IItineraryActivity } from '../../../Models/itinerary-activity.model';

const normalizeId = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value);

interface ScheduleInfo {
  itinerary: IItinerary2;
  itineraryActivity: IItineraryActivity;
  dayNumber: number;
  activityIndex: number; // Index in the activities array for unique identification
}

interface ItemCardScheduleInfoProps {
  activityId: string;
  itineraries: IItinerary2[];
  onDayChange?: (
    itineraryId: string,
    activityIdParam: string,
    currentDayNumber: number,
    newDayNumber: number
  ) => Promise<void>;
}

const ItemCardScheduleInfoComponent: React.FC<ItemCardScheduleInfoProps> = ({
  activityId,
  itineraries,
  onDayChange
}) => {
  const [editingMap, setEditingMap] = useState<Map<string, boolean>>(new Map());
  const [tempDayMap, setTempDayMap] = useState<Map<string, string>>(new Map());

  console.log('[ItemCardScheduleInfo] ── props ──────────────────────────────');
  console.log('[ItemCardScheduleInfo] activityId:', activityId);
  console.log(
    '[ItemCardScheduleInfo] onDayChange:',
    onDayChange ? '(provided)' : '(none)'
  );
  console.log(
    '[ItemCardScheduleInfo] itineraries count:',
    itineraries?.length ?? 0
  );
  itineraries?.forEach((itin, i) => {
    console.log(`[ItemCardScheduleInfo]   itinerary[${i}]:`, {
      id: itin.id,
      title: itin.title,
      deleted: itin.deleted,
      daysCount: itin.days?.length ?? 0,
      days: itin.days?.map((d) => ({
        dayNumber: d.dayNumber,
        activitiesCount: d.activities?.length ?? 0,
        activityIds: d.activities?.map((a) => a.activityId)
      }))
    });
  });
  console.log('[ItemCardScheduleInfo] ───────────────────────────────────────');

  // Find all itinerary activities that reference this trip activity
  const scheduleInfos: ScheduleInfo[] = [];

  itineraries.forEach((itinerary) => {
    console.log(
      '[ItemCardScheduleInfo] Checking itinerary:',
      itinerary.title,
      'days count:',
      itinerary.days?.length || 0
    );

    // Activities are nested inside days, not at the top level
    itinerary.days?.forEach((day) => {
      console.log(
        '[ItemCardScheduleInfo] Checking day:',
        day.dayNumber,
        'activities count:',
        day.activities?.length || 0
      );

      day.activities?.forEach((itinActivity, activityIndex) => {
        console.log(
          '[ItemCardScheduleInfo] Checking activity:',
          itinActivity.activityId,
          'against',
          activityId
        );
        if (normalizeId(itinActivity.activityId) === normalizeId(activityId)) {
          const dayNumber = day.dayNumber ?? 0;
          console.log('[ItemCardScheduleInfo] MATCH FOUND! Day:', dayNumber);

          scheduleInfos.push({
            itinerary,
            itineraryActivity: itinActivity,
            dayNumber,
            activityIndex
          });
        }
      });
    });
  });

  console.log(
    '[ItemCardScheduleInfo] Total schedule infos found:',
    scheduleInfos.length
  );

  const handleDayClick = (scheduleKey: string, currentDay: number) => {
    setEditingMap(new Map(editingMap.set(scheduleKey, true)));
    setTempDayMap(new Map(tempDayMap.set(scheduleKey, String(currentDay))));
  };

  const handleDayChange = (scheduleKey: string, value: string) => {
    setTempDayMap(new Map(tempDayMap.set(scheduleKey, value)));
  };

  const handleDayBlur = async (
    itineraryId: string,
    tripActivityId: string,
    currentDay: number,
    scheduleKey: string
  ) => {
    const newDayStr = tempDayMap.get(scheduleKey);
    const newDay = newDayStr ? parseInt(newDayStr, 10) : currentDay;

    if (!isNaN(newDay) && newDay !== currentDay && newDay >= 1) {
      // Call the onDayChange callback
      if (onDayChange) {
        try {
          await onDayChange(itineraryId, tripActivityId, currentDay, newDay);
        } catch (error) {
          console.error('Error rescheduling activity:', error);
        }
      }
    }

    // Exit edit mode
    const newEditMap = new Map(editingMap);
    newEditMap.delete(scheduleKey);
    setEditingMap(newEditMap);

    const newTempMap = new Map(tempDayMap);
    newTempMap.delete(scheduleKey);
    setTempDayMap(newTempMap);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    itineraryId: string,
    tripActivityId: string,
    currentDay: number,
    scheduleKey: string
  ) => {
    if (e.key === 'Enter') {
      handleDayBlur(itineraryId, tripActivityId, currentDay, scheduleKey);
    } else if (e.key === 'Escape') {
      const newEditMap = new Map(editingMap);
      newEditMap.delete(scheduleKey);
      setEditingMap(newEditMap);

      const newTempMap = new Map(tempDayMap);
      newTempMap.delete(scheduleKey);
      setTempDayMap(newTempMap);
    }
  };

  if (scheduleInfos.length === 0) {
    const unscheduledKey = 'unscheduled';
    const isEditing = editingMap.get(unscheduledKey) || false;

    return (
      <div className={styles.scheduleInfo}>
        <Calendar
          size={14}
          className={styles.scheduleIcon}
        />
        <div className={styles.scheduleList}>
          <span className={styles.scheduleItem}>
            {isEditing ? (
              <>
                <span className={styles.dayLabel}>Day </span>
                <input
                  type='number'
                  min='1'
                  value={tempDayMap.get(unscheduledKey) || '1'}
                  onChange={(e) =>
                    handleDayChange(unscheduledKey, e.target.value)
                  }
                  onBlur={async () => {
                    const newDayStr = tempDayMap.get(unscheduledKey);
                    const newDay = newDayStr ? parseInt(newDayStr, 10) : 1;

                    if (
                      !isNaN(newDay) &&
                      newDay >= 1 &&
                      onDayChange &&
                      itineraries[0]?.id
                    ) {
                      try {
                        await onDayChange(
                          itineraries[0].id,
                          activityId,
                          0,
                          newDay
                        );
                      } catch (error) {
                        console.error('Error scheduling activity:', error);
                      }
                    }

                    const newEditMap = new Map(editingMap);
                    newEditMap.delete(unscheduledKey);
                    setEditingMap(newEditMap);

                    const newTempMap = new Map(tempDayMap);
                    newTempMap.delete(unscheduledKey);
                    setTempDayMap(newTempMap);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      const newDayStr = tempDayMap.get(unscheduledKey);
                      const newDay = newDayStr ? parseInt(newDayStr, 10) : 1;

                      if (
                        !isNaN(newDay) &&
                        newDay >= 1 &&
                        onDayChange &&
                        itineraries[0]?.id
                      ) {
                        try {
                          await onDayChange(
                            itineraries[0].id,
                            activityId,
                            0,
                            newDay
                          );
                        } catch (error) {
                          console.error('Error scheduling activity:', error);
                        }
                      }

                      const newEditMap = new Map(editingMap);
                      newEditMap.delete(unscheduledKey);
                      setEditingMap(newEditMap);

                      const newTempMap = new Map(tempDayMap);
                      newTempMap.delete(unscheduledKey);
                      setTempDayMap(newTempMap);
                    } else if (e.key === 'Escape') {
                      const newEditMap = new Map(editingMap);
                      newEditMap.delete(unscheduledKey);
                      setEditingMap(newEditMap);

                      const newTempMap = new Map(tempDayMap);
                      newTempMap.delete(unscheduledKey);
                      setTempDayMap(newTempMap);
                    }
                  }}
                  className={styles.dayInput}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              </>
            ) : (
              <span
                className={styles.dayNumber}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingMap(new Map(editingMap.set(unscheduledKey, true)));
                  setTempDayMap(new Map(tempDayMap.set(unscheduledKey, '1')));
                }}
              >
                Not scheduled
              </span>
            )}
          </span>
        </div>
      </div>
    );
  }

  const uniqueDayNumbers = Array.from(
    new Set(
      scheduleInfos
        .map((info) => info.dayNumber)
        .filter((dayNumber) => dayNumber >= 1)
    )
  ).sort((a, b) => a - b);

  const primaryScheduleInfo = scheduleInfos[0];
  const aggregateScheduleKey = 'scheduled-aggregate';
  const isAggregateEditing = editingMap.get(aggregateScheduleKey) || false;
  const singleDayNumber =
    uniqueDayNumbers.length === 1 ? uniqueDayNumbers[0] : null;
  const dayNumbersText = uniqueDayNumbers.join(' & ');

  return (
    <div className={styles.scheduleInfo}>
      <Calendar
        size={14}
        className={styles.scheduleIcon}
      />
      <div className={styles.scheduleList}>
        <span className={styles.scheduleItem}>
          <span className={styles.itineraryName}>
            {primaryScheduleInfo.itinerary.title}
          </span>
          <span className={styles.dayLabel}> Day </span>
          {isAggregateEditing && singleDayNumber !== null ? (
            <input
              type='number'
              min='1'
              value={tempDayMap.get(aggregateScheduleKey) || singleDayNumber}
              onChange={(e) =>
                handleDayChange(aggregateScheduleKey, e.target.value)
              }
              onBlur={() =>
                handleDayBlur(
                  primaryScheduleInfo.itinerary.id,
                  activityId,
                  singleDayNumber,
                  aggregateScheduleKey
                )
              }
              onKeyDown={(e) =>
                handleKeyDown(
                  e,
                  primaryScheduleInfo.itinerary.id,
                  activityId,
                  singleDayNumber,
                  aggregateScheduleKey
                )
              }
              className={styles.dayInput}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className={styles.dayNumber}
              onClick={(e) => {
                if (singleDayNumber === null) return;
                e.stopPropagation();
                handleDayClick(aggregateScheduleKey, singleDayNumber);
              }}
            >
              {dayNumbersText}
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

// Memoize to prevent expensive re-computation when props haven't changed
export const ItemCardScheduleInfo = React.memo(
  ItemCardScheduleInfoComponent,
  (prevProps, nextProps) => {
    // Same activity ID and callback
    if (prevProps.activityId !== nextProps.activityId) return false;
    if (prevProps.onDayChange !== nextProps.onDayChange) return false;

    // Same itineraries reference or deep equal
    if (prevProps.itineraries === nextProps.itineraries) return true;
    if (prevProps.itineraries?.length !== nextProps.itineraries?.length)
      return false;

    // Don't do deep comparison - if the array reference changed, re-render
    // This is acceptable since itineraries don't change often
    return false;
  }
);
